import React, { useEffect, useState } from 'react';
import { Avatar, Button, Card, Descriptions, Input, Modal, Select, Space, Table, Tag, message } from 'antd';
import { EyeOutlined, ReloadOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '../services/api';

interface Member {
  id: string;
  userId: string;
  appType: string;
  appId: string;
  openId: string;
  unionId: string;
  phone: string;
  nickname: string;
  avatarUrl: string;
  bindStatus: string;
  lastLoginAt: string | null;
  lastLoginIp: string;
  createdAt: string;
  updatedAt: string;
}

interface MemberFilters {
  phone?: string;
  name?: string;
  bindStatus?: string;
}

const bindStatusMap: Record<string, { color: string; text: string }> = {
  bound: { color: 'green', text: '已绑定' },
  unbound: { color: 'default', text: '未绑定' },
  disabled: { color: 'red', text: '已禁用' },
};

const appTypeMap: Record<string, string> = {
  miniapp: '小程序',
  wechat_miniapp: '微信小程序',
  h5: 'H5',
};

const formatTime = (value?: string | null) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-');

const maskPhone = (phone: string) => {
  if (!phone || phone.length < 7) return phone || '-';
  return `${phone.substring(0, 3)}****${phone.substring(7)}`;
};

const renderBindStatus = (status: string) => {
  const config = bindStatusMap[status] || { color: 'default', text: status || '-' };
  return <Tag color={config.color}>{config.text}</Tag>;
};

const MemberManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchPhone, setSearchPhone] = useState('');
  const [searchName, setSearchName] = useState('');
  const [bindStatus, setBindStatus] = useState<string | undefined>();
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentMember, setCurrentMember] = useState<Member | null>(null);

  const fetchMembers = async (page = currentPage, size = pageSize, filters?: MemberFilters) => {
    const nextPhone = filters?.phone ?? searchPhone;
    const nextName = filters?.name ?? searchName;
    const nextBindStatus = filters?.bindStatus ?? bindStatus;

    setLoading(true);
    try {
      const response = await api.get('/member/list', {
        params: {
          page,
          pageSize: size,
          phone: nextPhone || undefined,
          name: nextName || undefined,
          bindStatus: nextBindStatus,
        },
      });
      setMembers(response.data.list || []);
      setTotal(response.data.total || 0);
      setCurrentPage(response.data.page || page);
      setPageSize(response.data.pageSize || size);
    } catch (error) {
      console.error('获取会员列表失败:', error);
      message.error('获取会员列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchMembers(1, pageSize);
  };

  const handleReset = () => {
    setSearchPhone('');
    setSearchName('');
    setBindStatus(undefined);
    setCurrentPage(1);
    fetchMembers(1, pageSize, { phone: '', name: '', bindStatus: undefined });
  };

  const handleView = async (record: Member) => {
    try {
      const response = await api.get(`/member/${record.id}`);
      setCurrentMember(response.data || record);
      setDetailVisible(true);
    } catch {
      setCurrentMember(record);
      setDetailVisible(true);
    }
  };

  const columns: ColumnsType<Member> = [
    {
      title: '会员信息',
      key: 'info',
      width: 240,
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={record.avatarUrl} />
          <div>
            <div style={{ fontWeight: 500 }}>{record.nickname || '微信用户'}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{maskPhone(record.phone)}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (phone: string) => phone || '-',
    },
    {
      title: '应用类型',
      dataIndex: 'appType',
      key: 'appType',
      width: 120,
      render: (value: string) => appTypeMap[value] || value || '-',
    },
    {
      title: 'OpenID',
      dataIndex: 'openId',
      key: 'openId',
      width: 260,
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    {
      title: 'UnionID',
      dataIndex: 'unionId',
      key: 'unionId',
      width: 220,
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    {
      title: '绑定状态',
      dataIndex: 'bindStatus',
      key: 'bindStatus',
      width: 110,
      render: renderBindStatus,
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 160,
      render: formatTime,
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: formatTime,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleView(record)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Card title="会员管理" style={{ marginBottom: 16 }}>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="手机号"
            value={searchPhone}
            onChange={(e) => setSearchPhone(e.target.value)}
            style={{ width: 160 }}
            allowClear
          />
          <Input
            placeholder="昵称"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            style={{ width: 160 }}
            allowClear
          />
          <Select
            placeholder="绑定状态"
            value={bindStatus}
            onChange={setBindStatus}
            allowClear
            style={{ width: 140 }}
            options={[
              { value: 'bound', label: '已绑定' },
              { value: 'unbound', label: '未绑定' },
              { value: 'disabled', label: '已禁用' },
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            重置
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={members}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1520 }}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (value) => `共 ${value} 条`,
            onChange: (page, size) => fetchMembers(page, size),
          }}
        />
      </Card>

      <Modal
        title="会员详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={<Button onClick={() => setDetailVisible(false)}>关闭</Button>}
        width={760}
      >
        {currentMember && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="昵称">{currentMember.nickname || '微信用户'}</Descriptions.Item>
            <Descriptions.Item label="手机号">{currentMember.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="应用类型">{appTypeMap[currentMember.appType] || currentMember.appType || '-'}</Descriptions.Item>
            <Descriptions.Item label="绑定状态">{renderBindStatus(currentMember.bindStatus)}</Descriptions.Item>
            <Descriptions.Item label="用户ID" span={2}>{currentMember.userId || '-'}</Descriptions.Item>
            <Descriptions.Item label="AppID" span={2}>{currentMember.appId || '-'}</Descriptions.Item>
            <Descriptions.Item label="OpenID" span={2}>{currentMember.openId || '-'}</Descriptions.Item>
            <Descriptions.Item label="UnionID" span={2}>{currentMember.unionId || '-'}</Descriptions.Item>
            <Descriptions.Item label="最后登录IP">{currentMember.lastLoginIp || '-'}</Descriptions.Item>
            <Descriptions.Item label="最后登录">{formatTime(currentMember.lastLoginAt)}</Descriptions.Item>
            <Descriptions.Item label="注册时间">{formatTime(currentMember.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{formatTime(currentMember.updatedAt)}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MemberManagement;
